/**
 * <s-product-thumbs> — shows one product photo at a time with clickable thumbnails.
 *
 * Required child markup:
 *   [data-product-gallery]        — horizontal scroll-snap track holding the slides.
 *   [data-gallery-slide="<id>"]   — one slide per media item.
 *   button[data-gallery-thumb="<id>"] — thumbnail that shows the matching slide.
 *
 * Clicking a thumbnail scrolls the track to its slide. Swiping or scrolling the track
 * keeps the active thumbnail (aria-current="true") in sync.
 */
class SProductThumbs extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const gallery = this.querySelector('[data-product-gallery]');
    if (!(gallery instanceof HTMLElement)) return;

    this.addEventListener('click', (event) => {
      const thumb = event.target instanceof Element ? event.target.closest('[data-gallery-thumb]') : null;
      if (!(thumb instanceof HTMLElement)) return;
      const slide = gallery.querySelector(`[data-gallery-slide="${thumb.dataset.galleryThumb}"]`);
      if (!(slide instanceof HTMLElement)) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      gallery.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.#setActive(thumb.dataset.galleryThumb);
    }, opts);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.target instanceof HTMLElement) {
          this.#setActive(entry.target.dataset.gallerySlide);
        }
      }
    }, { root: gallery, threshold: 0.6 });

    for (const slide of gallery.querySelectorAll('[data-gallery-slide]')) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #setActive(id) {
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      if (thumb.getAttribute('data-gallery-thumb') === id) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

customElements.define('s-product-thumbs', SProductThumbs);
